<template>
  <v-container>
    <v-row>
      <v-col cols="12" md="10" offset-md="1">
        <!-- Main Card Container -->
        <v-card class="my-5 elevation-2" min-height="600" style="border-radius: 12px; overflow: hidden">
          <v-card-title class="grey lighten-4 py-3 px-4">
            <v-btn
              text
              small
              @click="$router.back()"
              class="text-none grey--text text--darken-2"
            >
              <v-icon left>mdi-arrow-left</v-icon>
              Back
            </v-btn>
            <v-spacer />
            <!-- <v-btn
              v-if="payslip && payslip.status && !loading"
              small
              color="primary"
              elevation="0"
              class="text-none px-4"
              @click="downloadFile"
            >
              <v-icon left size="18">mdi-download</v-icon>
              Download PDF
            </v-btn> -->
          </v-card-title>

          <!-- Skeleton Loader while fetching state -->
          <v-card-text v-if="loading" class="pa-4">
            <v-skeleton-loader
              type="actions"
              class="mb-4"
            />
            <v-skeleton-loader
              type="image"
              height="700"
              class="rounded-lg"
            />
          </v-card-text>

          <!-- Error or Status Message -->
          <v-card-text
            v-else-if="payslip && !payslip.status"
            class="pa-12 text-center"
          >
            <v-icon size="80" color="orange lighten-2">mdi-alert-circle-outline</v-icon>
            <div class="mt-6 grey--text text--darken-2 font-weight-medium headline">
              Access Restricted
            </div>
            <div class="mt-2 grey--text subtitle-1">
              {{ payslip.message || "We couldn't retrieve your payslip at this time." }}
            </div>
          </v-card-text>

          <!-- PDF Viewer Iframe (Desktop) / Mobile Placeholder -->
          <v-card-text
            v-else-if="payslip && payslip.data"
            class="pa-0"
            :style="{
              height: isMobile ? 'auto' : '80vh',
              minHeight: isMobile ? '300px' : '700px',
              position: 'relative',
              background: isMobile ? '#ffffff' : '#525659'
            }"
          >
            <!-- Loader while Blob URL is being created/loaded -->
            <div v-if="!pdfUrl" class="pa-4 white" style="height: 100%">
              <v-skeleton-loader type="image" height="100%" />
            </div>
            
            <template v-else>
              <!-- Mobile Optimized View -->
              <div v-if="isMobile" class="pa-12 text-center d-flex flex-column align-center justify-center">
                <v-icon size="100" color="primary lighten-4">mdi-file-pdf-box</v-icon>
                <div class="mt-6 grey--text text--darken-2 headline font-weight-bold">
                  Payslip Preview
                </div>
                <div class="mt-2 grey--text subtitle-1 mb-8">
                  Your payslip is ready. Since you are on a mobile device, please use the button below to view it.
                </div>
                <v-btn
                  x-large
                  color="primary"
                  class="text-none px-8 rounded-lg shadow-lg"
                  @click="openPdfInNewTab"
                >
                  <v-icon left>mdi-eye</v-icon>
                  Open Payslip
                </v-btn>
                <div class="mt-4 grey--text caption">
                  The document will open in your device's native PDF viewer.
                </div>
              </div>

              <!-- Desktop Iframe View -->
              <iframe
                v-else
                :src="pdfUrl"
                width="100%"
                height="100%"
                frameborder="0"
                style="border: none; display: block"
                title="Payslip Viewer"
                loading="lazy"
              ></iframe>
            </template>
          </v-card-text>

          <!-- Fallback Empty State -->
          <v-card-text v-else class="pa-12 text-center">
             <v-icon size="80" color="grey lighten-2">mdi-file-pdf-outline</v-icon>
             <div class="mt-4 grey--text">No payslip data found for this period.</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import RouterMixin from "@kinetics254/cassandra-base/mixins/RouterMixin";

export default {
  name: "PayslipView",
  mixins: [RouterMixin],
  
  data() {
    return {
      pdfUrl: null,
    };
  },

  computed: {
    payslip() {
      return this.$store.getters["payslip/payslip"];
    },
    loading() {
      return this.$store.getters["loader/loading"];
    },
    filename() {
      const code = this.$route.params.code ? this.decodeRoute(this.$route.params.code) : 'Employee';
      return `${code} Payslip.pdf`.replace(/[/\\?%*:|"<>]/g, '-'); // Sanitize filename
    },
    isMobile() {
      // Production-grade mobile detection
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      ) || (window.innerWidth <= 768);
    }
  },

  watch: {
    'payslip.data': {
      immediate: true,
      handler(newData) {
        if (newData) {
          this.generatePdfPreview(newData);
        }
      }
    }
  },

  beforeRouteEnter(to, from, next) {
    next((v) => {
      const code = v.decodeRoute(to.params.code);
      v.$store.dispatch("payslip/getPayslip", code);
    });
  },

  beforeDestroy() {
    this.cleanupPdfUrl();
  },

  methods: {
    /**
     * Converts base64 to Blob URL for the iframe preview
     */
    generatePdfPreview(base64Data) {
      this.cleanupPdfUrl();
      try {
        const blob = this.base64ToBlob(base64Data, 'application/pdf');
        this.pdfUrl = URL.createObjectURL(blob);
      } catch (e) {
        console.error("[PayslipView] Preview generation failed:", e);
      }
    },

    /**
     * Revokes the object URL to prevent memory leaks
     */
    cleanupPdfUrl() {
      if (this.pdfUrl) {
        URL.revokeObjectURL(this.pdfUrl);
        this.pdfUrl = null;
      }
    },

    /**
     * Core conversion utility
     */
    base64ToBlob(base64, type = 'application/pdf') {
      const binStr = atob(base64);
      const len = binStr.length;
      const arr = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        arr[i] = binStr.charCodeAt(i);
      }
      return new Blob([arr], { type });
    },

    /**
     * Opens PDF in a new tab - most reliable for mobile devices
     */
    openPdfInNewTab() {
      if (!this.pdfUrl) return;
      window.open(this.pdfUrl, '_blank');
    },

    /**
     * Enterprise-grade download handler
     */
    downloadFile() {
      if (!this.payslip || !this.payslip.data) {
        if (this.$toast) {
          this.$toast.warning("No payslip data available for download.");
        }
        return;
      }

      try {
        const blob = this.base64ToBlob(this.payslip.data, 'application/pdf');

        // IE/Edge support
        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
          window.navigator.msSaveOrOpenBlob(blob, this.filename);
          return;
        }

        // Standard browsers
        const downloadUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.setAttribute("download", this.filename);
        
        // Ensure link is not visible
        link.style.display = 'none';
        document.body.appendChild(link);
        
        link.click();
        
        // Cleanup
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(downloadUrl), 200);
      } catch (error) {
        console.error("[PayslipView] Download failed:", error);
        if (this.$toast) {
          this.$toast.error("Failed to download the payslip. Please try again.");
        }
      }
    },
  },
};
</script>

<style scoped>
/* Ensure the iframe container looks neat */
iframe {
  border-top: 1px solid #e0e0e0;
}

/* Custom transitions for smoother UI feel */
.v-card {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

/* Scrollbar styling for the card if needed */
::-webkit-scrollbar {
  width: 6px;
}
::-webkit-scrollbar-track {
  background: #f1f1f1;
}
::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 10px;
}
::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.shadow-lg {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
}
</style>
